import {
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import { EditIntegrationDrawerContentProps } from '@modules/integrationsV2/types';
import { trackEditIntegrationEvent } from '@modules/integrationsV2/utils';
import { Box, Stack, TextField } from '@mui/material';
import { IntegrationChannelType } from 'generated/graphql';
import { useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { AddOrEditIntegrationDrawerFooter } from '../AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '../AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '../IntegrationDescriptionForm';

export const EditAppStoreIntegrationDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationType,
  description,
  integrationChannel,
}: EditIntegrationDrawerContentProps) => {
  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.Appstore,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
        trackEditIntegrationEvent({
          eventName: 'Edit Integration Submitted',
          channelType: IntegrationChannelType.Appstore,
          integrationID,
        });
      },
    });

  const [privateAPIKey, setPrivateAPIKey] = useState<string | undefined>();
  const [descriptionInput, setDescriptionInput] = useState(description);
  const [issuerID, setIssuerID] = useState<string | undefined>();
  const [privateAPIKeyID, setPrivateAPIKeyID] = useState<string | undefined>();

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.Appstore,
    integrationType,
    hasValidFormFields: true, // privateAPIKEy is optional
    descriptionInput,
    currentDescription: description,
    onValidated: async () => {
      updateIntegration({
        integrationID,
        isActive: true,
        updateData: {
          appstore: {
            privateAPIKey,
            issuerID,
            privateAPIKeyID,
          },
        },
        updateDescription: {
          description: descriptionInput.trim(),
        },
      });
    },
  });

  const handlePrivateAPIKeyChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPrivateAPIKey(event.target.value);
  };

  const isLoading = IsLoading(updateIntegrationStatus);

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title="Edit Appstore Integration"
        />
        <Stack justifyContent="space-between">
          <Stack p={3} spacing={2} justifyContent="space-between">
            <TextField
              fullWidth
              autoComplete="new-password"
              type="password"
              label="Private API Key (Optional)"
              name="privateAPIKey"
              placeholder="Enter Private API Key"
              multiline
              maxRows={4}
              value={privateAPIKey}
              disabled={isLoading}
              variant="outlined"
              onChange={handlePrivateAPIKeyChange}
              helperText="Adding API Key allows Enterpret to fetch detailed metadata associated with each review."
            />
            <TextField
              fullWidth
              autoComplete="new-password"
              type="password"
              label="Private API Key ID (Optional)"
              name="privateAPIKeyID"
              placeholder="Enter Private API Key ID"
              value={privateAPIKeyID}
              disabled={isLoading}
              variant="outlined"
              onChange={(event) => {
                setPrivateAPIKeyID(event.target.value);
              }}
            />
            <TextField
              fullWidth
              type="text"
              label="Issuer ID (Optional)"
              name="issuerID"
              placeholder="Enter Issuer ID"
              value={issuerID}
              disabled={isLoading}
              variant="outlined"
              onChange={(event) => {
                setIssuerID(event.target.value);
              }}
            />
            <IntegrationDescriptionForm
              value={descriptionInput}
              onChange={setDescriptionInput}
              isRequired={isDescriptionRequired}
              validatedDescriptionMessage={validatedDescriptionMessage}
              isValidationInProgress={isValidatingDescription}
              descriptionValidForQuestions={descriptionValidForQuestions}
              isPreValidatedDescription={isPreValidatedDescription}
              integrationType={integrationType}
            />
          </Stack>
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        isDisabled={isDisabled}
        isLoading={isLoading}
      />
    </Stack>
  );
};
